/**
 * Sessão de staff da Plataforma (D-10.5 / D-01, Task 10).
 *
 * A Plataforma é um app interno: só `operator`/`admin` entram. Há TRÊS
 * provedores atrás da MESMA interface, escolhidos pelo ambiente:
 *
 * - `SupabaseStaffSessionProvider`: caminho real e completo —
 *   **login** (`grant_type=password`), **restauração** da sessão a partir do
 *   `sessionStorage`, **renovação** (`grant_type=refresh_token`) e logout.
 *   Está "verde em código, aguardando secret": sem `VITE_SUPABASE_URL` e
 *   `VITE_SUPABASE_ANON_KEY` ele não é instanciado.
 * - `SimulatorStaffSessionProvider`: **somente em desenvolvimento/teste**,
 *   quando a credencial não existe. Determinístico, sem segredo, marcado em
 *   `source: "simulator"` para a UI avisar o ambiente simulado.
 * - `UnconfiguredStaffSessionProvider`: build de **produção sem credencial**.
 *   Falha FECHADO (nega e explica) — um app de produção mal configurado nunca
 *   recebe sessão simulada de admin (D-10.22).
 *
 * Autorização: o papel de staff é lido **apenas** de `app_metadata.role`, que
 * é controlado pelo servidor. `user_metadata` é editável pelo próprio usuário
 * e por isso NÃO serve como autorização (D-10.23). A autoridade final continua
 * sendo o backend: RLS + `profiles` (migration 0018). Este guard é a borda de
 * UI, não a fonte de permissão.
 *
 * Débito conhecido e registrado (handoff de hardening de auth pós-R2, ver
 * `phase-R2-plataforma-decisoes.md`): o Worker ainda proxya as Edge Functions
 * com a anon key e `profiles.role` não é espelhado em `app_metadata`. Enquanto
 * isso, este login é barreira de **UI** — a autorização de verdade das rotas é
 * trabalho da task de hardening, não desta.
 *
 * Nada aqui grava segredo: tokens vivem só na sessão do browser
 * (`sessionStorage`), nunca no repositório, nos logs ou em fixtures.
 */

export type StaffRole = "operator" | "admin";

export interface StaffSession {
  userId: string;
  email: string;
  role: StaffRole;
  /** Token para o `Authorization: Bearer` do PlatformApiClient. */
  accessToken: string | null;
  source: "supabase" | "simulator";
}

export type StaffSessionSource = "supabase" | "simulator" | "unconfigured";

export interface StaffSessionProvider {
  /** Sessão atual ou `null` quando não há usuário de staff autenticado. */
  getSession(): Promise<StaffSession | null>;
  /** Autentica com credenciais de staff. Lança erro legível em falha. */
  signIn(email: string, password: string): Promise<StaffSession | null>;
  signOut(): Promise<void>;
  /** Token corrente, sem I/O — usado pelo cliente de API a cada request. */
  currentAccessToken(): string | null;
  /** `true` quando a UI deve oferecer tela de login ao não achar sessão. */
  readonly supportsLogin: boolean;
  readonly source: StaffSessionSource;
}

export const STAFF_ROLES: readonly StaffRole[] = ["operator", "admin"];

export function isStaffRole(value: unknown): value is StaffRole {
  return typeof value === "string" && (STAFF_ROLES as readonly string[]).includes(value);
}

/** Chave do token no `sessionStorage` (não persiste entre sessões do browser). */
export const STAFF_TOKEN_STORAGE_KEY = "super2.plataforma.staff_token";

/** Renova o token quando falta menos que isto para expirar. */
export const TOKEN_REFRESH_SKEW_MS = 60_000;

export const AUTH_NOT_CONFIGURED_MESSAGE =
  "Autenticação da Plataforma não configurada neste build (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY ausentes). Acesso negado.";

export interface StaffTokenBundle {
  accessToken: string;
  /** `null` quando o provedor não devolveu refresh token. */
  refreshToken: string | null;
  /** Instante de expiração em epoch ms; `null` = desconhecido. */
  expiresAt: number | null;
}

export interface StaffTokenStore {
  read(): StaffTokenBundle | null;
  write(bundle: StaffTokenBundle): void;
  clear(): void;
}

function isBundle(value: unknown): value is StaffTokenBundle {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.accessToken === "string" && candidate.accessToken.length > 0;
}

/**
 * Armazenamento real do browser. Conteúdo corrompido é descartado (e limpo)
 * em vez de derrubar a tela: sessão inválida é "sem sessão", não erro fatal.
 */
export function browserStaffTokenStore(): StaffTokenStore {
  return {
    read() {
      if (typeof sessionStorage === "undefined") return null;
      const raw = sessionStorage.getItem(STAFF_TOKEN_STORAGE_KEY);
      if (!raw) return null;
      try {
        const parsed: unknown = JSON.parse(raw);
        if (!isBundle(parsed)) {
          sessionStorage.removeItem(STAFF_TOKEN_STORAGE_KEY);
          return null;
        }
        return {
          accessToken: parsed.accessToken,
          refreshToken: typeof parsed.refreshToken === "string" ? parsed.refreshToken : null,
          expiresAt: typeof parsed.expiresAt === "number" ? parsed.expiresAt : null
        };
      } catch {
        sessionStorage.removeItem(STAFF_TOKEN_STORAGE_KEY);
        return null;
      }
    },
    write(bundle) {
      if (typeof sessionStorage === "undefined") return;
      sessionStorage.setItem(STAFF_TOKEN_STORAGE_KEY, JSON.stringify(bundle));
    },
    clear() {
      if (typeof sessionStorage === "undefined") return;
      sessionStorage.removeItem(STAFF_TOKEN_STORAGE_KEY);
    }
  };
}

export interface AuthHttpResponse {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}

export type AuthFetch = (
  url: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string }
) => Promise<AuthHttpResponse>;

export interface SupabaseAuthConfig {
  supabaseUrl: string;
  anonKey: string;
  fetchImpl?: AuthFetch;
  tokenStore?: StaffTokenStore;
  /** Injetável no teste para simular expiração sem esperar o tempo real. */
  now?: () => number;
}

interface GrantPayload {
  access_token?: unknown;
  refresh_token?: unknown;
  expires_in?: unknown;
  expires_at?: unknown;
}

function bundleFromGrant(payload: unknown, nowMs: number): StaffTokenBundle | null {
  if (typeof payload !== "object" || payload === null) return null;
  const grant = payload as GrantPayload;
  if (typeof grant.access_token !== "string" || !grant.access_token) return null;
  const expiresAt =
    typeof grant.expires_at === "number"
      ? grant.expires_at * 1000
      : typeof grant.expires_in === "number"
        ? nowMs + grant.expires_in * 1000
        : null;
  return {
    accessToken: grant.access_token,
    refreshToken: typeof grant.refresh_token === "string" ? grant.refresh_token : null,
    expiresAt
  };
}

/**
 * Caminho real de autenticação: Supabase Auth (GoTrue) via REST, sem SDK —
 * o app já tem um `fetch` tipado e não precisa de outra dependência.
 */
export class SupabaseStaffSessionProvider implements StaffSessionProvider {
  readonly source = "supabase" as const;
  readonly supportsLogin = true;
  private readonly store: StaffTokenStore;
  private readonly baseUrl: string;

  constructor(private readonly config: SupabaseAuthConfig) {
    this.store = config.tokenStore ?? browserStaffTokenStore();
    this.baseUrl = config.supabaseUrl.replace(/\/$/, "");
  }

  private get fetchImpl(): AuthFetch {
    const injected = this.config.fetchImpl;
    if (injected) return injected;
    const globalFetch = (globalThis as { fetch?: AuthFetch }).fetch;
    if (!globalFetch) throw new Error("fetch_unavailable: pass SupabaseAuthConfig.fetchImpl");
    // `this.fetchImpl(...)` chama o getter e invoca o retorno como método DESTE
    // objeto — sem o bind, o `fetch` nativo recebe `this` = SupabaseStaffSessionProvider
    // em vez de `window`/`globalThis`, e o browser recusa com "Illegal invocation"
    // (só apareceu em produção: dev sempre usava a sessão simulada, nunca este
    // caminho real de login).
    return globalFetch.bind(globalThis);
  }

  private now(): number {
    return this.config.now ? this.config.now() : Date.now();
  }

  currentAccessToken(): string | null {
    return this.store.read()?.accessToken ?? null;
  }

  /** Login com e-mail/senha de staff. Credencial inválida vira erro legível. */
  async signIn(email: string, password: string): Promise<StaffSession | null> {
    const response = await this.fetchImpl(`${this.baseUrl}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: { apikey: this.config.anonKey, "content-type": "application/json" },
      body: JSON.stringify({ email, password })
    });
    if (!response.ok) {
      // Mensagem genérica de propósito: não revela se o e-mail existe.
      throw new Error("Não foi possível entrar com essas credenciais de operação.");
    }
    const bundle = bundleFromGrant(await response.json(), this.now());
    if (!bundle) throw new Error("Resposta de autenticação sem token de acesso.");
    this.store.write(bundle);
    const session = await this.getSession();
    if (!session) {
      // Autenticou, mas não é staff: não deixa token de não-staff na sessão.
      await this.signOut();
      throw new Error("Esta conta não tem papel de operação (operator/admin) na Plataforma.");
    }
    return session;
  }

  /**
   * Restaura a sessão do armazenamento do browser, renovando o token quando
   * necessário, e confirma o papel de staff no servidor.
   */
  async getSession(): Promise<StaffSession | null> {
    const bundle = await this.usableBundle();
    if (!bundle) return null;
    const response = await this.fetchImpl(`${this.baseUrl}/auth/v1/user`, {
      headers: { apikey: this.config.anonKey, authorization: `Bearer ${bundle.accessToken}` }
    });
    if (!response.ok) {
      // Token recusado pelo servidor: sessão não vale mais nada no browser.
      this.store.clear();
      return null;
    }
    const user = (await response.json()) as {
      id?: string;
      email?: string;
      app_metadata?: { role?: unknown };
    };
    // SOMENTE `app_metadata` (server-controlled). `user_metadata` é editável
    // pelo usuário e nunca concede papel aqui.
    const role = isStaffRole(user.app_metadata?.role) ? user.app_metadata?.role : null;
    if (!user.id || !role) return null;
    return {
      userId: user.id,
      email: user.email ?? "",
      role,
      accessToken: bundle.accessToken,
      source: "supabase"
    };
  }

  /** Token válido (renovando se preciso) ou `null` se não houver sessão. */
  private async usableBundle(): Promise<StaffTokenBundle | null> {
    const bundle = this.store.read();
    if (!bundle) return null;
    const expiring = bundle.expiresAt !== null && bundle.expiresAt - this.now() <= TOKEN_REFRESH_SKEW_MS;
    if (!expiring) return bundle;
    if (!bundle.refreshToken) {
      this.store.clear();
      return null;
    }
    try {
      const response = await this.fetchImpl(`${this.baseUrl}/auth/v1/token?grant_type=refresh_token`, {
        method: "POST",
        headers: { apikey: this.config.anonKey, "content-type": "application/json" },
        body: JSON.stringify({ refresh_token: bundle.refreshToken })
      });
      if (!response.ok) {
        this.store.clear();
        return null;
      }
      const renewed = bundleFromGrant(await response.json(), this.now());
      if (!renewed) {
        this.store.clear();
        return null;
      }
      this.store.write(renewed);
      return renewed;
    } catch {
      // Falha de rede na renovação: trata como sessão encerrada (fecha).
      this.store.clear();
      return null;
    }
  }

  async signOut(): Promise<void> {
    const bundle = this.store.read();
    this.store.clear();
    if (!bundle) return;
    try {
      // Revoga o refresh token no servidor; falha aqui não reabre a sessão
      // local, que já foi limpa acima.
      await this.fetchImpl(`${this.baseUrl}/auth/v1/logout`, {
        method: "POST",
        headers: { apikey: this.config.anonKey, authorization: `Bearer ${bundle.accessToken}` }
      });
    } catch {
      /* sessão local já encerrada; nada a fazer */
    }
  }
}

/**
 * Sessão simulada de staff (**só dev/teste**, sem credencial configurada).
 * Mesmo contrato do provedor real; `accessToken: null` porque não existe token
 * verdadeiro — o cliente de API simplesmente não manda `Authorization`.
 */
export class SimulatorStaffSessionProvider implements StaffSessionProvider {
  readonly source = "simulator" as const;
  readonly supportsLogin = false;
  private signedOut = false;

  constructor(private readonly session: StaffSession = {
    userId: "staff_simulado_001",
    email: "operacao@super2.local",
    role: "admin",
    accessToken: null,
    source: "simulator"
  }) {}

  currentAccessToken(): string | null {
    return null;
  }

  async getSession(): Promise<StaffSession | null> {
    return this.signedOut ? null : this.session;
  }

  /** Não há credencial a verificar em dev: reabre a sessão simulada. */
  async signIn(): Promise<StaffSession | null> {
    this.signedOut = false;
    return this.session;
  }

  async signOut(): Promise<void> {
    this.signedOut = true;
  }
}

/**
 * Build de produção sem credencial de auth: **falha fechado**. Nenhuma sessão,
 * nenhum simulador, mensagem explícita de configuração ausente.
 */
export class UnconfiguredStaffSessionProvider implements StaffSessionProvider {
  readonly source = "unconfigured" as const;
  readonly supportsLogin = false;

  currentAccessToken(): string | null {
    return null;
  }

  async getSession(): Promise<StaffSession | null> {
    throw new Error(AUTH_NOT_CONFIGURED_MESSAGE);
  }

  // Assinatura completa (e parâmetros ignorados) para o contrato bater: aqui
  // não existe credencial a verificar, então qualquer tentativa é negada.
  async signIn(_email: string, _password: string): Promise<StaffSession | null> {
    void _email;
    void _password;
    throw new Error(AUTH_NOT_CONFIGURED_MESSAGE);
  }

  async signOut(): Promise<void> {
    /* não há sessão a encerrar */
  }
}

export interface StaffSessionEnv {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_ANON_KEY?: string;
  /** `import.meta.env.DEV` do Vite. */
  DEV?: boolean;
  /** `import.meta.env.MODE` do Vite (`development`/`test`/`production`). */
  MODE?: string;
}

/** Dev/teste explícito — jamais inferido por ausência de configuração. */
export function isDevelopmentEnv(env?: StaffSessionEnv): boolean {
  if (env?.DEV === true) return true;
  const mode = env?.MODE?.trim().toLowerCase();
  return mode === "development" || mode === "test";
}

/**
 * Escolhe o provedor pelo ambiente. A tela consome sempre
 * `StaffSessionProvider`, então a troca de um para o outro não muda uma linha
 * de UI — mas a escolha NÃO é só "tem credencial?": sem credencial, apenas
 * dev/teste recebe simulador; produção nega (D-10.22).
 */
export function createStaffSessionProvider(env?: StaffSessionEnv): StaffSessionProvider {
  const supabaseUrl = env?.VITE_SUPABASE_URL?.trim();
  const anonKey = env?.VITE_SUPABASE_ANON_KEY?.trim();
  if (supabaseUrl && anonKey) {
    return new SupabaseStaffSessionProvider({ supabaseUrl, anonKey });
  }
  if (isDevelopmentEnv(env)) {
    return new SimulatorStaffSessionProvider();
  }
  return new UnconfiguredStaffSessionProvider();
}
